import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    DimensionValue,
    LayoutChangeEvent,
    Platform,
    Pressable,
    StyleProp,
    StyleSheet,
    TextStyle,
    View,
    Text,
    ViewStyle,
} from "react-native";
import { useTheme } from "../hooks/use-theme";
import loaderLottie from "../assets/lottie/loader.json";
import { LiftTheme } from "../theme";
import Lottie from "lottie-react-native";
import Icon, { IconName } from "./Icon";
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
    Easing,
} from "react-native-reanimated";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";

export type ButtonSize = "small" | "medium" | "large";
export type ButtonMode =
    | "primary"
    | "outlined"
    | "secondary"
    | "tertiary"
    | "success"
    | "danger"
    | "warning"
    | "link"
    | "large-link"
    | "light"
    | "soft"
    | "disabled";

export type ButtonProps = {
    label: string;
    disabled?: boolean;
    processing?: boolean;
    shrinked?: boolean;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    onPress?: () => void | Promise<any>;
    style?: StyleProp<ViewStyle> | undefined;
    buttonMode?: ButtonMode;
    size?: ButtonSize;
    textSize?: ButtonSize;
    shrinkToIcon?: IconName;
    testID?: string;
    leftIcon?: IconName;
    /** Defaults to 12, which is small next to a `large` button's label. */
    leftIconSize?: number;
    adjustsTextFontSizeToFit?: boolean;
};

const getDisabledTheme = (theme: Pick<LiftTheme, "colors">) => ({
    button: {
        backgroundColor: theme.colors.backdrop,
        borderColor: theme.colors.backdrop,
    },
    text: {
        color: theme.colors.onSurfaceDisabled,
    },
});

const SIZE_STYLE_MAP: { [key in ButtonSize]: (theme: LiftTheme) => ViewStyle } = {
    small: () => ({
        height: 22,
        borderRadius: 11,
    }),
    medium: () => ({
        height: 44,
        borderRadius: 22,
    }),
    large: () => ({
        height: 60,
        borderRadius: 30,
    }),
};

const SIZE_LABEL_STYLE_MAP: {
    [key in ButtonSize]: (theme: LiftTheme) => StyleProp<TextStyle>;
} = {
    small: () => ({
        fontSize: 12,
        //lineHeight: 14,
    }),
    medium: () => ({
        fontSize: 14,
        //lineHeight: 17,
    }),
    large: () => ({
        fontSize: 16,
        //lineHeight: 24,
    }),
};

const SIZE_PROGRESS_IMAGE: {
    [key in ButtonSize]: number;
} = {
    small: 40,
    medium: 60,
    large: 80,
};

type ButtonStyle = {
    button?: StyleProp<ViewStyle>;
    text?: StyleProp<TextStyle>;
    disabled?: StyleProp<ViewStyle>;
    leftIcon?: StyleProp<ViewStyle> & { color?: string };
};

const MODE_STYLE_MAP: { [key in ButtonMode]: (theme: LiftTheme) => ButtonStyle } = {
    primary: (theme) => ({
        button: {
            backgroundColor: theme.colors.primary,
            borderColor: theme.colors.primary,
        },
        disabled: {},
        text: {
            color: theme.colors.background,
        },
    }),
    outlined: (theme) => ({
        button: {
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.primary,
        },
        disabled: {},
        text: {
            color: theme.colors.primary,
        },
    }),
    secondary: (theme) => ({
        button: {
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
        },
        disabled: {},
        text: {
            color: theme.colors.background,
        },
        // Without this the icon falls back to its default dark color, unreadable on the
        // saturated background.
        leftIcon: {
            color: theme.colors.background,
        },
    }),
    tertiary: (theme) => ({
        button: {
            backgroundColor: theme.colors.tertiary,
            borderColor: theme.colors.tertiary,
        },
        disabled: {},
        text: {
            color: theme.colors.background,
        },
    }),
    success: (theme) => ({
        button: {
            backgroundColor: theme.colors.success,
            borderColor: theme.colors.success,
        },
        disabled: {},
        text: {
            color: theme.colors.background,
        },
    }),
    danger: (theme) => ({
        button: {
            backgroundColor: theme.colors.error,
            borderColor: theme.colors.error,
        },
        disabled: {},
        text: {
            color: theme.colors.background,
        },
    }),
    warning: (theme) => ({
        button: {
            backgroundColor: theme.colors.warning,
            borderColor: theme.colors.warning,
        },
        disabled: {},
        text: {
            color: theme.colors.background,
        },
    }),
    link: (theme) => ({
        button: {
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.background,
            alignItems: "flex-start",
            justifyContent: "flex-start",
            paddingHorizontal: 0,
            elevation: 0,
        },
        disabled: {
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.background,
        },
        text: {
            fontFamily: "Inter-SemiBold",
            color: theme.colors.secondary,
            textTransform: "none",
            letterSpacing: 0,
        },
    }),
    "large-link": (theme) => ({
        button: {
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.background,
            alignItems: "center",
            justifyContent: "flex-start",
            paddingHorizontal: 0,
            elevation: 0,
        },
        disabled: {
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.background,
        },
        text: {
            fontFamily: "Inter-Regular",
            color: theme.colors.secondary,
            textTransform: "none",
            letterSpacing: 0,
        },
    }),
    light: (theme) => ({
        button: {
            backgroundColor: theme.colors.accentLighter,
            paddingHorizontal: 7,
            borderWidth: 0,
            alignItems: "center",
            justifyContent: "center",
        },
        text: {
            fontFamily: "Inter-Regular",
            alignItems: "center",
            verticalAlign: "middle",
            justifyContent: "center",
            alignSelf: "center",
            textAlignVertical: "center",
            color: theme.colors.secondary,
            textTransform: "none",
            letterSpacing: 0,
        },
        leftIcon: {
            color: theme.colors.secondary,
            marginRight: 5,
        },
    }),
    soft: (theme) => ({
        button: {
            backgroundColor: theme.colors.lighterGrey,
            borderWidth: 0,
        },
        text: {
            color: theme.colors.body,
        },
    }),
    disabled: (theme) => getDisabledTheme(theme),
};

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const AnimatedIcon = Animated.createAnimatedComponent(Icon);

export const BUTTON_SHRINK_DURATION = 300;

const Button = ({
    style,
    buttonMode = "primary",
    label,
    processing = false,
    disabled = false,
    shrinked,
    onPress,
    size = "medium",
    textSize = size,
    shrinkToIcon,
    testID,
    leftIcon,
    leftIconSize = 12,
    adjustsTextFontSizeToFit = true,
    ref,
}: ButtonProps & { ref?: React.Ref<View> }) => {
    const styles = useStyles();
    const theme = useTheme();
    const isLarge = useIsScreenSizeMin("large");

    const width = useSharedValue(0);

    const textOpacity = useSharedValue(1);
    const textWidth = useSharedValue<DimensionValue>("100%");

    const iconOpacity = useSharedValue(0);

    const [buttonInitialWidth, setButtonInitialWidth] = useState<number>();

    const animatedButtonStyles = useAnimatedStyle(() => ({
        width: width.value,
    }));
    const animatedTextStyles = useAnimatedStyle(() => ({
        opacity: textOpacity.value,
        width: textWidth.value,
    }));
    const animatedIconStyles = useAnimatedStyle(() => ({
        opacity: iconOpacity.value,
    }));

    useEffect(() => {
        if (!isLarge && buttonInitialWidth && shrinkToIcon) {
            width.value = withTiming(
                shrinked ? (SIZE_STYLE_MAP[size](theme).height as number) : buttonInitialWidth,
                {
                    duration: BUTTON_SHRINK_DURATION,
                },
            );

            const easingToVisible = Easing.in(Easing.exp);
            const easingToHidden = Easing.out(Easing.exp);

            textOpacity.value = withTiming(shrinked ? 0 : 1, {
                duration: BUTTON_SHRINK_DURATION,
                easing: shrinked ? easingToHidden : easingToVisible,
            });
            textWidth.value = withTiming(shrinked ? "0%" : "100%", {
                duration: BUTTON_SHRINK_DURATION,
            });

            iconOpacity.value = withTiming(shrinked ? 1 : 0, {
                duration: BUTTON_SHRINK_DURATION,
                easing: shrinked ? easingToVisible : easingToHidden,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shrinked, shrinkToIcon]);

    const onLayout = useCallback(
        (e: LayoutChangeEvent) => {
            if (shrinked !== undefined && !buttonInitialWidth && shrinkToIcon) {
                const initialWidth = e.nativeEvent.layout.width;
                setButtonInitialWidth(initialWidth);
                width.value = withTiming(initialWidth, {
                    duration: 0,
                });
            }
        },
        [buttonInitialWidth, shrinked, width, shrinkToIcon],
    );

    const adjustsFontSizeToFit = useMemo(
        () =>
            adjustsTextFontSizeToFit &&
            (Platform.OS !== "android" || !shrinkToIcon) &&
            buttonMode !== "light",
        [adjustsTextFontSizeToFit, buttonMode, shrinkToIcon],
    );

    return (
        <AnimatedPressable
            ref={ref}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onPress={processing ? undefined : onPress}
            disabled={disabled || processing}
            onLayout={onLayout}
            style={[
                styles.button,
                MODE_STYLE_MAP[buttonMode](theme).button,
                SIZE_STYLE_MAP[size](theme),
                disabled && styles.disabled,
                disabled && MODE_STYLE_MAP[buttonMode](theme).disabled,
                { alignSelf: shrinkToIcon ? "flex-end" : "stretch" },
                style,
                shrinkToIcon && { overflow: "visible", ...theme.shadow },
                buttonInitialWidth && animatedButtonStyles,
            ]}
            id={testID}
            testID={testID}
        >
            {!processing ? (
                <>
                    {leftIcon && (
                        <Icon
                            name={leftIcon}
                            size={leftIconSize}
                            style={MODE_STYLE_MAP[buttonMode](theme).leftIcon}
                            color={MODE_STYLE_MAP[buttonMode](theme).leftIcon?.color}
                        />
                    )}
                    <Animated.Text
                        numberOfLines={1}
                        adjustsFontSizeToFit={adjustsFontSizeToFit}
                        style={[
                            styles.text,
                            MODE_STYLE_MAP[buttonMode](theme).text,
                            SIZE_LABEL_STYLE_MAP[textSize](theme),
                            processing ? { opacity: 0.5 } : {},
                            disabled ? styles.disabledText : {},
                            shrinkToIcon && animatedTextStyles,
                        ]}
                    >
                        {label}
                    </Animated.Text>
                    {shrinkToIcon && (
                        <AnimatedIcon
                            name={shrinkToIcon}
                            color="white"
                            style={[{ position: "absolute", right: 11 }, animatedIconStyles]}
                            size={30}
                        />
                    )}
                </>
            ) : (
                <Lottie
                    autoPlay
                    loop
                    style={[styles.loader, { height: SIZE_PROGRESS_IMAGE[size] }]}
                    source={loaderLottie}
                />
            )}
        </AnimatedPressable>
    );
};

const useStyles = () => {
    const { colors, gridSize } = useTheme();
    const { button: disabledButton, text: disabledText } = getDisabledTheme({ colors });

    return useMemo(
        () =>
            StyleSheet.create({
                button: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    paddingHorizontal: gridSize / 2,
                    borderWidth: 2,
                },
                disabled: disabledButton,
                text: {
                    fontFamily: "Inter-Bold",
                    fontSize: 14,
                    letterSpacing: 1.68,
                    //lineHeight: 16.8,
                    textTransform: "uppercase",
                    flexGrow: 1,
                    textAlign: "center",
                    textAlignVertical: "center",
                },
                disabledText: disabledText,
                loader: {
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                },
                revertButton: {
                    backgroundColor: colors.background,
                    borderWidth: 2,
                    borderColor: colors.primary,
                },
                revertText: {
                    color: colors.primary,
                },
            }),
        [colors.background, colors.primary, disabledButton, disabledText, gridSize],
    );
};

export default Button;
